import React, { useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOpleiding, aanmeldenOpleiding, getTrainingReviews, getMyTrainingReview } from '../services/opleidingenApi';
import { useAuthStore } from '../store/authStore';
import { TrainingReviewModal } from '../components/TrainingReviewModal';
import {
  ArrowLeft, BookOpen, MapPin, Calendar, Clock, Award, Users,
  Video, Download, CheckCircle, AlertCircle, Globe, Star, ChevronDown, ChevronUp, MessageSquare
} from 'lucide-react';

export const OpleidingDetailPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [opmerkingen, setOpmerkingen] = useState('');
  const [showAanmeldForm, setShowAanmeldForm] = useState(false);
  const [aanmeldSuccess, setAanmeldSuccess] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(searchParams.get('review') === '1');
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [expandedReview, setExpandedReview] = useState<number | null>(null);

  const { data: training, isLoading, isError } = useQuery({
    queryKey: ['opleiding', id],
    queryFn: () => getOpleiding(parseInt(id!)),
    enabled: !!id,
  });

  const { data: reviewsData } = useQuery({
    queryKey: ['training-reviews', id],
    queryFn: () => getTrainingReviews(parseInt(id!)),
    enabled: !!id,
  });

  const { data: myReview } = useQuery({
    queryKey: ['my-training-review', id],
    queryFn: () => getMyTrainingReview(parseInt(id!)),
    enabled: !!id && !!user,
  });

  const aanmeldenMutation = useMutation({
    mutationFn: () => aanmeldenOpleiding(parseInt(id!), opmerkingen || undefined),
    onSuccess: () => {
      setAanmeldSuccess(true);
      setShowAanmeldForm(false);
      queryClient.invalidateQueries({ queryKey: ['mijn-aanmeldingen'] });
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10 animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/3" />
        <div className="h-64 bg-gray-200 rounded-xl" />
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (isError || !training) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('opleidingDetail.notFoundTitle')}</h2>
        <Link to="/opleidingen" className="text-[#0B1F3A] hover:underline">{t('opleidingDetail.backToList')}</Link>
      </div>
    );
  }

  const isProfessional = user?.role === 'professional';

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('opleidingDetail.backToList')}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {training.omslagfoto_url ? (
                <img src={training.omslagfoto_url} alt={training.titel} className="w-full h-56 object-cover" />
              ) : (
                <div className="w-full h-56 bg-gradient-to-br from-[#0B1F3A]/10 to-[#0B1F3A]/20 flex items-center justify-center">
                  <BookOpen className="w-16 h-16 text-[#0B1F3A]/30" />
                </div>
              )}
              <div className="p-6">
                {/* Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {training.soort_opleiding && (
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full">
                      {training.soort_opleiding}
                    </span>
                  )}
                  {training.niveau && (
                    <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">
                      {training.niveau}
                    </span>
                  )}
                  {training.duur && (
                    <span className="bg-yellow-50 text-yellow-800 text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {training.duur}
                    </span>
                  )}
                </div>

                <h1 className="text-2xl font-bold text-gray-900 mb-3">{training.titel}</h1>

                {/* Provider */}
                <div className="flex items-center gap-3 py-3 border-t border-b border-gray-100 mb-4">
                  {training.provider_logo ? (
                    <img src={training.provider_logo} alt={training.organisatie_naam} className="h-8 w-auto object-contain" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-[#0B1F3A]/10 flex items-center justify-center">
                      <span className="text-sm font-bold text-[#0B1F3A]">{training.organisatie_naam?.charAt(0)}</span>
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{training.organisatie_naam}</div>
                    {training.aanbieder_bio && (
                      <div className="text-xs text-gray-500">{training.aanbieder_bio}</div>
                    )}
                  </div>
                  {training.provider_website && (
                    <a
                      href={training.provider_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto text-xs text-[#0B1F3A] hover:underline flex items-center gap-1"
                    >
                      <Globe className="w-3 h-3" /> {t('opleidingDetail.website')}
                    </a>
                  )}
                </div>

                {training.samenvatting && (
                  <p className="text-gray-700 leading-relaxed">{training.samenvatting}</p>
                )}
              </div>
            </div>

            {/* Inhoud & Resultaat */}
            {(training.leerdoelen || training.doelgroep || training.programma_onderdelen) && (
              <Section title={t('opleidingDetail.sectionContent')}>
                {training.leerdoelen && (
                  <SubSection label={t('opleidingDetail.subLearningGoals')}>
                    <div className="whitespace-pre-line text-gray-700 text-sm">{training.leerdoelen}</div>
                  </SubSection>
                )}
                {training.doelgroep && (
                  <SubSection label={t('opleidingDetail.subTargetGroup')}>
                    <div className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{training.doelgroep}</span>
                    </div>
                  </SubSection>
                )}
                {training.programma_onderdelen && (
                  <SubSection label={t('opleidingDetail.subProgram')}>
                    <div className="whitespace-pre-line text-gray-700 text-sm">{training.programma_onderdelen}</div>
                  </SubSection>
                )}
              </Section>
            )}

            {/* Praktische details */}
            <Section title={t('opleidingDetail.sectionPractical')}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {training.datum_tijd && (
                  <DetailItem icon={<Calendar className="w-4 h-4" />} label={t('opleidingDetail.subDateTime')} value={training.datum_tijd} />
                )}
                {training.locatie_detail && (
                  <DetailItem icon={<MapPin className="w-4 h-4" />} label={t('opleidingDetail.subLocation')} value={training.locatie_detail} />
                )}
                {training.studiebelasting && (
                  <DetailItem icon={<Clock className="w-4 h-4" />} label={t('opleidingDetail.subStudyLoad')} value={training.studiebelasting} />
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                {training.inclusief_materialen && (
                  <span className="flex items-center gap-1.5 text-sm text-green-700 bg-green-50 px-3 py-1 rounded-full">
                    <CheckCircle className="w-3.5 h-3.5" /> {t('opleidingDetail.materialsIncluded')}
                  </span>
                )}
                {training.lunch_inbegrepen && (
                  <span className="flex items-center gap-1.5 text-sm text-green-700 bg-green-50 px-3 py-1 rounded-full">
                    <CheckCircle className="w-3.5 h-3.5" /> {t('opleidingDetail.lunchIncluded')}
                  </span>
                )}
              </div>
              {training.studiemateriaal_info && (
                <div className="mt-3 text-sm text-gray-600">
                  <span className="font-medium">{t('opleidingDetail.studyMaterial')} </span>{training.studiemateriaal_info}
                </div>
              )}
            </Section>

            {/* Erkenning & Kwaliteit */}
            {(training.accreditatie || training.certificering || training.docenten || training.toelatingseisen) && (
              <Section title={t('opleidingDetail.sectionQuality')}>
                {training.accreditatie && (
                  <SubSection label={t('opleidingDetail.subAccreditation')}>
                    <div className="flex items-start gap-2">
                      <Award className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{training.accreditatie}</span>
                    </div>
                  </SubSection>
                )}
                {training.certificering && (
                  <SubSection label={t('opleidingDetail.subCertification')}>
                    <span className="text-gray-700 text-sm">{training.certificering}</span>
                  </SubSection>
                )}
                {training.docenten && (
                  <SubSection label={t('opleidingDetail.subTeacher')}>
                    <span className="text-gray-700 text-sm">{training.docenten}</span>
                  </SubSection>
                )}
                {training.toelatingseisen && (
                  <SubSection label={t('opleidingDetail.subAdmission')}>
                    <span className="text-gray-700 text-sm">{training.toelatingseisen}</span>
                  </SubSection>
                )}
              </Section>
            )}

            {/* Reviews sectie */}
            <ReviewsSection
              trainingId={parseInt(id!)}
              trainingTitel={training.titel}
              reviewsData={reviewsData}
              myReview={myReview ?? null}
              user={user}
              showReviewModal={showReviewModal}
              setShowReviewModal={setShowReviewModal}
              showAllReviews={showAllReviews}
              setShowAllReviews={setShowAllReviews}
              expandedReview={expandedReview}
              setExpandedReview={setExpandedReview}
              t={t}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Prijs & Aanmelden card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-20">
              <div className="mb-4 pb-4 border-b border-gray-100">
                <div className="text-sm text-gray-500 mb-1">{t('opleidingDetail.investment')}</div>
                {training.prijs_excl_btw != null ? (
                  <div className="text-3xl font-bold text-[#0B1F3A]">
                    €{parseFloat(String(training.prijs_excl_btw)).toFixed(2).replace('.', ',')}
                  </div>
                ) : (
                  <div className="text-lg font-semibold text-gray-600">{t('opleidingDetail.priceOnRequest')}</div>
                )}
                <div className="mt-1 text-xs text-green-600 font-medium flex items-center gap-1">
                  <CheckCircle className="w-3.5 h-3.5" />
                  {t('opleidingDetail.belagomPrice')}
                </div>
              </div>

              {aanmeldSuccess ? (
                <div className="text-center py-4">
                  <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">{t('opleidingDetail.registrationReceived')}</p>
                  <p className="text-sm text-gray-500 mt-1">{t('opleidingDetail.providerContact')}</p>
                  <Link to="/mijn-opleidingen" className="mt-3 block text-sm text-[#0B1F3A] hover:underline">
                    {t('opleidingDetail.viewRegistrations')}
                  </Link>
                </div>
              ) : showAanmeldForm ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('opleidingDetail.remarksLabel')}
                  </label>
                  <textarea
                    value={opmerkingen}
                    onChange={(e) => setOpmerkingen(e.target.value)}
                    rows={3}
                    placeholder={t('opleidingDetail.remarksPlaceholder')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] resize-none"
                  />
                  {aanmeldenMutation.isError && (
                    <p className="text-sm text-red-600 mt-2">
                      {(aanmeldenMutation.error as any)?.response?.data?.message || t('opleidingDetail.registerError')}
                    </p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => aanmeldenMutation.mutate()}
                      disabled={aanmeldenMutation.isPending}
                      className="flex-1 bg-[#0B1F3A] text-white py-2.5 rounded-lg font-medium hover:bg-[#1A3459] transition-colors disabled:opacity-60"
                    >
                      {aanmeldenMutation.isPending ? t('opleidingDetail.registering') : t('opleidingDetail.register')}
                    </button>
                    <button
                      onClick={() => setShowAanmeldForm(false)}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                    >
                      {t('opleidingDetail.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {isProfessional ? (
                    <button
                      onClick={() => setShowAanmeldForm(true)}
                      className="w-full bg-[#0B1F3A] text-white py-3 rounded-lg font-semibold hover:bg-[#1A3459] transition-colors"
                    >
                      {t('opleidingDetail.register')}
                    </button>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-gray-500 mb-3">{t('opleidingDetail.loginPrompt')}</p>
                      <Link
                        to="/login"
                        className="block w-full bg-[#0B1F3A] text-white py-3 rounded-lg font-semibold hover:bg-[#1A3459] transition-colors text-center"
                      >
                        {t('opleidingDetail.login')}
                      </Link>
                    </div>
                  )}
                </>
              )}

              {/* Media bijlagen */}
              <div className="mt-5 pt-4 border-t border-gray-100 space-y-2">
                {training.brochure_url && (
                  <a
                    href={training.brochure_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#0B1F3A] hover:text-[#E5A425] font-medium"
                  >
                    <Download className="w-4 h-4" />
                    {t('opleidingDetail.downloadBrochure')}
                  </a>
                )}
                {training.video_url && (
                  <a
                    href={training.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-[#0B1F3A] hover:text-[#E5A425] font-medium"
                  >
                    <Video className="w-4 h-4" />
                    {t('opleidingDetail.watchVideo')}
                  </a>
                )}
              </div>
            </div>

            {/* Quick facts */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">{t('opleidingDetail.quickFactsTitle')}</h3>
              <div className="space-y-3">
                {training.wanneer && (
                  <QuickFact icon={<Calendar className="w-4 h-4 text-gray-400" />} label={t('opleidingDetail.qfWhen')} value={training.wanneer} />
                )}
                {training.locaties && (
                  <QuickFact icon={<MapPin className="w-4 h-4 text-gray-400" />} label={t('opleidingDetail.qfLocation')} value={training.locaties} />
                )}
                {training.voor_wie && (
                  <QuickFact icon={<Users className="w-4 h-4 text-gray-400" />} label={t('opleidingDetail.qfForWho')} value={training.voor_wie} />
                )}
                {training.duur && (
                  <QuickFact icon={<Clock className="w-4 h-4 text-gray-400" />} label={t('opleidingDetail.qfDuration')} value={training.duur} />
                )}
                {training.certificering && (
                  <QuickFact icon={<Award className="w-4 h-4 text-gray-400" />} label={t('opleidingDetail.qfCertificate')} value={training.certificering} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-4">{title}</h2>
    <div className="space-y-4">{children}</div>
  </div>
);

const SubSection: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-sm font-medium text-gray-500 mb-1">{label}</div>
    {children}
  </div>
);

const DetailItem: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="text-gray-400 mt-0.5">{icon}</div>
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm text-gray-800 font-medium">{value}</div>
    </div>
  </div>
);

const QuickFact: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    {icon}
    <div>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-sm text-gray-800">{value}</div>
    </div>
  </div>
);

const StarDisplay: React.FC<{ value: number; size?: 'sm' | 'md' }> = ({ value, size = 'sm' }) => {
  const cls = size === 'md' ? 'w-5 h-5' : 'w-3.5 h-3.5';
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`${cls} ${s <= Math.round(value) ? 'fill-[#E5A425] text-[#E5A425]' : 'text-gray-200'}`}
        />
      ))}
    </span>
  );
};

interface ReviewsSectionProps {
  trainingId: number;
  trainingTitel: string;
  reviewsData: { reviews: any[]; averages: any; count: number } | undefined;
  myReview: any;
  user: any;
  showReviewModal: boolean;
  setShowReviewModal: (v: boolean) => void;
  showAllReviews: boolean;
  setShowAllReviews: (v: boolean) => void;
  expandedReview: number | null;
  setExpandedReview: (id: number | null) => void;
  t: (key: string, opts?: any) => string;
}

const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  trainingId, trainingTitel, reviewsData, myReview, user,
  showReviewModal, setShowReviewModal, showAllReviews, setShowAllReviews,
  expandedReview, setExpandedReview, t,
}) => {
  const REVIEW_LABELS_PROFESSIONAL = [
    { key: 'relevantie', label: t('opleidingDetail.rlRelevance') },
    { key: 'toepasbaarheid', label: t('opleidingDetail.rlApplicability') },
    { key: 'expertise', label: t('opleidingDetail.rlExpertise') },
    { key: 'structuur', label: t('opleidingDetail.rlStructure') },
    { key: 'prijs_kwaliteit', label: t('opleidingDetail.rlValueForMoney') },
    { key: 'algemene_ervaring', label: t('opleidingDetail.rlOverallExp') },
    { key: 'aanbeveling', label: t('opleidingDetail.rlRecommendation') },
  ] as const;

  const REVIEW_LABELS_EMPLOYEE = [
    { key: 'relevantie', label: t('opleidingDetail.rlRelevance') },
    { key: 'toepasbaarheid', label: t('opleidingDetail.rlApplicability') },
    { key: 'expertise', label: t('opleidingDetail.rlExpertise') },
    { key: 'structuur', label: t('opleidingDetail.rlStructure') },
    { key: 'prijs_kwaliteit', label: t('opleidingDetail.rlGrowthValue') },
    { key: 'algemene_ervaring', label: t('opleidingDetail.rlOverallExp') },
    { key: 'aanbeveling', label: t('opleidingDetail.rlRecommendation') },
  ] as const;

  const canReview =
    user &&
    (user.role === 'professional' || user.role === 'employer_member' || user.role === 'opdrachtgever') &&
    !myReview;

  const reviews = reviewsData?.reviews ?? [];
  const averages = reviewsData?.averages;
  const count = reviewsData?.count ?? 0;
  const displayedReviews = showAllReviews ? reviews : reviews.slice(0, 3);

  return (
    <>
      {showReviewModal && (
        <TrainingReviewModal
          trainingId={trainingId}
          trainingTitel={trainingTitel}
          onClose={() => setShowReviewModal(false)}
        />
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900">{t('opleidingDetail.reviewsTitle')}</h2>
            {count > 0 && averages?.overall != null && (
              <div className="flex items-center gap-1.5 bg-[#E5A425]/10 px-2.5 py-1 rounded-full">
                <Star className="w-4 h-4 fill-[#E5A425] text-[#E5A425]" />
                <span className="font-bold text-gray-900 text-sm">{averages.overall.toFixed(1)}</span>
                <span className="text-gray-500 text-xs">({count})</span>
              </div>
            )}
          </div>
          {canReview && (
            <button
              onClick={() => setShowReviewModal(true)}
              className="flex items-center gap-2 bg-[#0B1F3A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3459] transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              {t('opleidingDetail.writeReview')}
            </button>
          )}
          {myReview && (
            <span className="text-xs text-green-600 bg-green-50 px-3 py-1 rounded-full font-medium flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" /> {t('opleidingDetail.alreadyReviewed')}
            </span>
          )}
        </div>

        {/* Average breakdown */}
        {count > 0 && averages && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 p-4 bg-gray-50 rounded-xl">
            {REVIEW_LABELS_PROFESSIONAL.map(({ key, label }) => (
              <div key={key} className="flex flex-col gap-1">
                <span className="text-xs text-gray-500">{label}</span>
                <div className="flex items-center gap-1.5">
                  <StarDisplay value={averages[key] ?? 0} />
                  <span className="text-xs font-semibold text-gray-700">{averages[key]?.toFixed(1) ?? '–'}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Review list */}
        {count === 0 ? (
          <div className="text-center py-8">
            <Star className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">{t('opleidingDetail.noReviews')}</p>
            {canReview && (
              <p className="text-gray-400 text-xs mt-1">{t('opleidingDetail.beFirst')}</p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {displayedReviews.map((review) => {
              const labels = review.reviewer_type === 'employee' ? REVIEW_LABELS_EMPLOYEE : REVIEW_LABELS_PROFESSIONAL;
              const isExpanded = expandedReview === review.id;
              const overallScore = Math.round(Number(review.score ?? 0) * 10) / 10;
              const anoniem = review.reviewer_type === 'medewerker';

              return (
                <div key={review.id} className="border border-gray-100 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#0B1F3A]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-[#0B1F3A]">
                          {anoniem ? '·' : <>{review.first_name?.charAt(0)}{review.last_name?.charAt(0)}</>}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 text-sm">
                          {anoniem ? t('opleidingDetail.anonymousEmployee') : <>{review.first_name} {review.last_name}</>}
                          {review.company_name && (
                            <span className="text-gray-500 font-normal"> · {review.company_name}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <StarDisplay value={overallScore} />
                          <span className="text-xs font-semibold text-gray-600">{overallScore.toFixed(1)}</span>
                          <span className="text-xs text-gray-400">
                            {new Date(review.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setExpandedReview(isExpanded ? null : review.id)}
                      className="flex items-center gap-1 text-xs text-[#0B1F3A] hover:underline flex-shrink-0"
                    >
                      {isExpanded
                        ? <><ChevronUp className="w-3.5 h-3.5" /> {t('opleidingDetail.reviewLess')}</>
                        : <><ChevronDown className="w-3.5 h-3.5" /> {t('opleidingDetail.reviewDetails')}</>}
                    </button>
                  </div>

                  {review.open_comment && (
                    <p className="mt-3 text-sm text-gray-700 italic pl-12">"{review.open_comment}"</p>
                  )}

                  {isExpanded && (
                    <div className="mt-3 pl-12 grid grid-cols-2 gap-x-6 gap-y-2">
                      {labels.filter(({ key }) => review[key] != null).map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">{label}</span>
                          <div className="flex items-center gap-1">
                            <StarDisplay value={review[key] ?? 0} />
                            <span className="text-xs font-medium text-gray-700">{review[key]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {reviews.length > 3 && (
              <button
                onClick={() => setShowAllReviews(!showAllReviews)}
                className="w-full py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {showAllReviews ? t('opleidingDetail.showLess') : t('opleidingDetail.showAll', { count })}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default OpleidingDetailPage;
